import { createIcons, ArrowLeft, Download, Printer, Edit, CheckCircle2, FileText, Shield, Sparkles, ZoomIn, ZoomOut, Maximize2, Share2, Eye, RefreshCw, PenTool, XCircle, AlertTriangle, X } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { router } from "../router/router.js";
import type { IntegrityReportId } from "../../shared/kernel/identifiers.js";
import { validateReportForPrinting, type ReportValidationResult } from "../../domain/reports/report-validator.js";
import { UnifiedInspectionIntegrityReportMapper } from "../../application/mappers/unified-inspection-integrity-report-mapper.js";
import "../components/document/pdf-preview.js";

export class AppReportViewerPage extends HTMLElement {
  private reportId: string = '';
  private report: any = null;
  private equipmentSnapshot: any = null;
  private recommendations: any[] = [];
  private photos: any[] = [];
  private checklistComponents: any[] = [];
  private safetyDevices: any[] = [];
  private instruments: any[] = [];
  private cachedSnapshot: any = null;
  /** STUDIO INSPECT IA: logo da versão visual deste RI (para o mapeador local de reserva). */
  private riLogo: string | null = null;
    private zoomLevel: number = 100;
  private isLoading: boolean = true;
  private errorMsg: string | null = null;

  static get observedAttributes() {
    return ['report-id'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'report-id' && newValue) {
      this.reportId = newValue;
      this.loadData();
    }
  }

  private onChecklistUpdated = (e: any) => {
    if (!e.detail || !e.detail.reportId || e.detail.reportId === this.reportId || (this.report?.inspectionId && e.detail.inspectionId === this.report.inspectionId)) {
      this.cachedSnapshot = null;
      this.loadData();
    }
  };

  private closeReportViewer = (e?: Event) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Fallback explícito: ir para a inspeção que gerou este RI.
    if (this.report?.inspectionId) {
      router.navigate(`/inspections/checklist/${this.report.inspectionId}`);
    } else {
      router.navigate('/reports/integrity');
    }
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      this.closeReportViewer();
    }
  };

  connectedCallback() {
    window.addEventListener('ri_checklist_updated', this.onChecklistUpdated);
    window.addEventListener('keydown', this.handleKeyDown);
    const parts = window.location.pathname.split('/');
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart !== 'view' && !this.reportId) {
      this.reportId = lastPart;
    }
    this.loadData();
  }

  disconnectedCallback() {
    window.removeEventListener('ri_checklist_updated', this.onChecklistUpdated);
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  private async loadData() {
    this.cachedSnapshot = null;
    this.isLoading = true;
    this.errorMsg = null;
    this.render();

    try {
      const p = (window as any).app?.platform;
      const activeClientId = contextStore.get().activeClientId;
      let ctx = { activeClientId, userId: contextStore.get().userId, permissions: contextStore.get().permissions } as any;

      if (!this.reportId) {
        // Fallback to first available report
        const list = await p.reportService.listReports(ctx);
        if (list && list.length > 0) {
          this.reportId = list[0].id;
        } else {
          throw new Error("Nenhum relatório de integridade disponível.");
        }
      }

      this.report = await p.reportService.getReportById(ctx, this.reportId as IntegrityReportId);
      if (!this.report) throw new Error(`Relatório "${this.reportId}" não encontrado.`);

      // Sync document context with report's actual clientId
      if (this.report.clientId) {
        if (this.report.clientId !== contextStore.get().activeClientId) {
          contextStore.set({ activeClientId: this.report.clientId });
        }
        ctx = { activeClientId: this.report.clientId, userId: contextStore.get().userId, permissions: contextStore.get().permissions } as any;
      }

      console.log("[REAL-PDF-FLOW]", {
        module: "RI",
        stage: "HANDLER_START",
        handler: "loadData",
        reportId: this.reportId,
        clientId: this.report.clientId
      });

      // Obter o DocumentSnapshot autoritativo diretamente do backend
      if (p.reportService?.buildDocumentSnapshot) {
        try {
          this.cachedSnapshot = await p.reportService.buildDocumentSnapshot(ctx, this.reportId as IntegrityReportId);
          console.log("[REAL-PDF-FLOW]", {
            module: "RI",
            stage: "SNAPSHOT_RECEIVED",
            reportId: this.reportId,
            snapshotType: this.cachedSnapshot?.type,
            dataKeys: Object.keys(this.cachedSnapshot?.data || {})
          });
        } catch (err) {
          console.warn("Could not build document snapshot from service:", err);
        }
      }

      // Load Equipment and Inspection details
      let currentEquipmentId = null;
      if (this.report.equipmentSnapshotId && p.equipmentService) {
        try {
          this.equipmentSnapshot = await p.equipmentService.getSnapshot(ctx, this.report.equipmentSnapshotId);
          currentEquipmentId = this.equipmentSnapshot?.equipmentId || this.equipmentSnapshot?.equipmentData?.id;
        } catch {
          this.equipmentSnapshot = null;
        }
      }

      if (this.report.inspectionId) {
         try {
            const insp = await p.inspectionService.getInspectionById(ctx, this.report.inspectionId);
            if (insp && insp.equipmentId) {
               currentEquipmentId = insp.equipmentId;
               if (!this.equipmentSnapshot) {
                 this.equipmentSnapshot = await p.equipmentService.getEquipment(ctx, insp.equipmentId);
               }
            }
         } catch(e) { }
      }

      // Always synchronize with latest master equipment to ensure modified equipment properties & new fields are reflected, UNLESS report is historical
      const isHistorical = this.report.status === 'SIGNED' || this.report.status === 'COMPLETED';
      if (!isHistorical && currentEquipmentId && p.equipmentService) {
        try {
          const liveEq = await p.equipmentService.getEquipment(ctx, currentEquipmentId);
          if (liveEq) {
            const prevData = this.equipmentSnapshot?.equipmentData || this.equipmentSnapshot || {};
            const mergedProperties = {
              ...(prevData.properties || {}),
              ...(liveEq.properties || {})
            };
            const mergedData = {
              ...prevData,
              ...liveEq,
              properties: mergedProperties
            };
            this.equipmentSnapshot = {
              ...(this.equipmentSnapshot || {}),
              equipmentId: currentEquipmentId,
              equipmentData: mergedData,
              clientName: liveEq.clientName || this.equipmentSnapshot?.clientName,
              projectName: liveEq.projectName || this.equipmentSnapshot?.projectName,
              unitAreaName: liveEq.unitAreaName || this.equipmentSnapshot?.unitAreaName
            };
          }
        } catch {}
      }


      // Load Recommendations
      if (p.reportService) {
        try {
          this.recommendations = await p.reportService.listRecommendations(ctx, this.reportId as IntegrityReportId);
        } catch {
          this.recommendations = [];
        }
      }

      // Load Photos from Checklist
      this.photos = [];
      if (this.report.inspectionId && p.inspectionService) {
        try {
          const chkState = await p.inspectionService.getChecklistState(ctx, this.report.inspectionId);
          if (chkState?.photos && chkState.photos.length > 0) {
            this.photos = [...chkState.photos];
          }
        } catch {
          this.photos = [];
        }
      }

      // Clean up legacy report checklist key if found
      try {
        localStorage.removeItem(`ri_checklist_${this.reportId}`);
        localStorage.removeItem(`ri_cache_${this.reportId}`);
      } catch {
        // ignore
      }


      // Load Safety Devices and Instruments if applicable
      if (currentEquipmentId) {
        if (p.safetyDeviceService) {
          try {
            this.safetyDevices = await p.safetyDeviceService.listSafetyDevices(ctx, { protectedEquipmentId: currentEquipmentId });
          } catch {
             this.safetyDevices = [];
          }
        }
        if (p.instrumentService) {
          try {
            this.instruments = await p.instrumentService.listInstruments(ctx, { equipmentId: currentEquipmentId });
          } catch {
             this.instruments = [];
          }
        }
      }

      // STRICT ARCHITECTURAL RULE: Load Checklist ONLY from the technical inspection!
      this.checklistComponents = [];
      if (this.report.inspectionId && p.inspectionService) {
        try {
          const res = await p.inspectionService.getChecklistState(ctx, this.report.inspectionId);

          if (res && res.checklist && res.components && res.components.length > 0) {
            this.checklistComponents = res.components.map((c: any) => ({
              ...c,
              name: c.name || c.groupId || c.definitionComponentId || 'Componente',
              recommendations: c.recommendations || []
            }));
          }
        } catch (err) {
          console.warn("Could not load checklist from service", err);
        }
      }

      // Check inspection-level local cache if repo had not synced yet or to merge recommendations
      if (this.report.inspectionId) {
        const storedInspChk = localStorage.getItem(`ri_checklist_insp_${this.report.inspectionId}`);
        if (storedInspChk) {
          try {
            const parsed = JSON.parse(storedInspChk);
            if (Array.isArray(parsed) && parsed.length > 0) {
              if (this.checklistComponents.length === 0) {
                this.checklistComponents = parsed.map((item: any) => ({
                  id: item.id,
                  name: item.name,
                  groupId: item.name,
                  definitionComponentId: item.name,
                  result: item.status === 'APPROVED' ? 'APPROVED' : (item.status === 'REJECTED' ? 'REJECTED' : (item.status === 'NOT_APPLICABLE' ? 'NOT_APPLICABLE' : (item.status === 'CLIENT_DISCRETION' ? 'CLIENT_DISCRETION' : 'PENDING'))),
                  description: item.condition || (item.status === 'APPROVED' ? 'Sem anomalias (Aprovado)' : 'Anomalia registrada'),
                  inspectionNotes: item.technicalComment || '',
                  recommendations: item.recommendations || []
                }));
              } else {
                // Merge recommendations and comments from local cache
                this.checklistComponents = this.checklistComponents.map(comp => {
                  const compName = (comp as any).name || (comp as any).groupId || (comp as any).definitionComponentId;
                  const matchCache = parsed.find((p: any) => p.id === comp.id || p.name === compName);
                  return {
                    ...comp,
                    name: compName,
                    recommendations: (comp as any).recommendations?.length > 0 ? (comp as any).recommendations : (matchCache?.recommendations || [])
                  };
                });
              }
            }
          } catch (e) {
            console.warn("Could not parse inspection checklist for RI", e);
          }
        }
      }

      // Ensure all recommendations are linked to components
      if (this.recommendations && this.recommendations.length > 0) {
        this.checklistComponents = this.checklistComponents.map(comp => {
          const compName = ((comp as any).name || comp.groupId || comp.definitionComponentId || '').toLowerCase().trim();
          const compId = (comp.id || '').toLowerCase();
          const existingRecs = (comp as any).recommendations || [];
          if (existingRecs.length > 0) {
            const matched = this.recommendations.filter(r => {
              if (r.checklistItemId && (r.checklistItemId === comp.id || (r.checklistItemId as string).toLowerCase() === compId)) return true;
              if (r.componentId && (r.componentId === comp.id || (r.componentId as string).toLowerCase() === compId)) return true;
              if ((r as any).componentName && (r as any).componentName.toLowerCase().trim() === compName) return true;
              if (r.scope && compName && (r.scope.toLowerCase().startsWith(compName + ':') || r.scope.toLowerCase().startsWith(compName + ' -'))) return true;
              return false;
            });
            if (matched.length > 0) {
              for (const existing of existingRecs) {
                const matchReal = matched.find(m => m.id === existing.id || m.code === existing.code || m.recommendationNumber === existing.code);
                if (matchReal) {
                  if (existing.zr !== (matchReal.zr || undefined)) {
                    existing.zr = matchReal.zr || undefined;
                  }
                  if (matchReal.scope) {
                    const cleanedScope = compName && matchReal.scope.toLowerCase().startsWith(compName + ':')
                      ? matchReal.scope.slice(compName.length + 1).trim()
                      : matchReal.scope;
                    existing.description = cleanedScope;
                  }
                }
              }
            }
            return comp;
          } else {
            const matched = this.recommendations.filter(r => {
              if (r.checklistItemId && (r.checklistItemId === comp.id || (r.checklistItemId as string).toLowerCase() === compId)) return true;
              if (r.componentId && (r.componentId === comp.id || (r.componentId as string).toLowerCase() === compId)) return true;
              if ((r as any).componentName && (r as any).componentName.toLowerCase().trim() === compName) return true;
              if (r.scope && compName && (r.scope.toLowerCase().startsWith(compName + ':') || r.scope.toLowerCase().startsWith(compName + ' -'))) return true;
              return false;
            });
            if (matched.length > 0) {
              return {
                ...comp,
                recommendations: matched.map(m => ({
                  id: m.id,
                  code: m.code || m.recommendationNumber,
                  description: m.scope,
                  criticality: m.criticality,
                  discipline: m.discipline,
                  execution: m.executeIn === 'SHUTDOWN' ? 'PARADA' : 'ROTINA',
                  zr: m.zr || undefined
                }))
              };
            }
          }
          return comp;
        });
      }

      if (p.reportService?.buildDocumentSnapshot) {
        try {
          this.cachedSnapshot = await p.reportService.buildDocumentSnapshot(ctx, this.reportId as IntegrityReportId);
        } catch (e) {
          console.warn("Could not build document snapshot via backend service, will use local mapper", e);
        }
      }
      if (!this.cachedSnapshot && this.reportId) {
        const closed = ["APPROVED", "AWAITING_SIGNATURE", "SIGNED", "COMPLETED"].includes(String(this.report?.status)) || !!this.report?.finalization;
        const visual = await p.studioDesignService?.resolveDocumentVisual(ctx, "RI", String(this.reportId), closed).catch(() => null);
        this.riLogo = visual?.logoImage ?? null;
      }

      this.isLoading = false;
      this.render();
    } catch (e: any) {
      this.isLoading = false;
      this.errorMsg = e.message;
      this.render();
    }
  }

  private getValidation(): ReportValidationResult {
    return validateReportForPrinting(
      this.report,
      this.equipmentSnapshot?.equipmentData,
      this.checklistComponents
    );
  }

  private getSnapshotData() {
    if (this.cachedSnapshot) {
      return this.cachedSnapshot;
    }

    return UnifiedInspectionIntegrityReportMapper.mapToDocumentSnapshot({
      tenantLogo: this.riLogo,
      report: this.report,
      inspection: this.report?.inspection,
      equipmentSnapshot: this.equipmentSnapshot,
      safetyDevices: this.safetyDevices,
      instruments: this.instruments,
      checklistComponents: this.checklistComponents,
      performedInspectionMethods: (this.report as any)?.performedInspectionMethods || [],
      refDocs: (this.report as any)?.refDocs || [],
      recommendations: this.recommendations,
      photos: this.photos,
      client: { name: contextStore.get().activeClientName || "Cliente" }
    });
  }

  private showPrintBlockedModal(validation: ReportValidationResult) {
    const existing = this.querySelector('#print-blocked-modal');
    if (existing) existing.remove();

    const modalDiv = document.createElement('div');
    modalDiv.id = 'print-blocked-modal';
    modalDiv.innerHTML = `
      <div style="position:fixed; inset:0; background:rgba(0,0,0,0.8); display:flex; align-items:center; justify-content:center; z-index:99999; backdrop-filter:blur(4px);">
        <div style="background:var(--color-graphite-800); border:2px solid var(--status-warning-solid); border-radius:12px; padding:28px; max-width:540px; width:92%; color: var(--color-text-primary); box-shadow:0 25px 50px -12px rgba(245,158,11,0.35);">
          <div style="display:flex; align-items:center; gap:12px; margin-bottom:16px; color: var(--status-warning-text);">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
            <div style="font-size:1.15rem; font-weight:800; letter-spacing:0.02em;">AVISO DE PENDÊNCIAS TÉCNICAS</div>
          </div>
          <div style="font-size:0.875rem; color:var(--color-graphite-300); line-height:1.5; margin-bottom:16px;">
            Este laudo contém campos em branco ou pendências técnicas não resolvidas. O download foi autorizado a seu pedido, mas recomenda-se correção antes da emissão oficial.
          </div>
          <div style="background:rgba(239,68,68,0.12); border:1px solid rgba(239,68,68,0.3); border-radius:6px; padding:14px; margin-bottom:20px;">
            <div style="font-weight:700; color:var(--status-danger-border); font-size:0.85rem; margin-bottom:8px;">Pendências e inconsistências identificadas:</div>
            <ul style="margin:0; padding-left:20px; font-size:0.825rem; color:var(--status-danger-bg); display:flex; flex-direction:column; gap:5px;">
              ${validation.errors.map(err => `<li>${err}</li>`).join('')}
            </ul>
          </div>
          <div style="display:flex; justify-content:flex-end; gap:10px;">
            <button type="button" id="btn-close-blocked-modal" style="background:var(--color-graphite-700); border:none; color: var(--color-text-primary); padding:8px 16px; border-radius:6px; font-weight:600; font-size:0.875rem; cursor:pointer;">
              Fechar
            </button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modalDiv);

    modalDiv.querySelector('#btn-close-blocked-modal')?.addEventListener('click', () => {
      modalDiv.remove();
    });
  }

  private handlePrint() {
    console.log("[REAL-PDF-FLOW]", {
      module: "RI",
      stage: "CLICK",
      action: "print",
      reportId: this.reportId
    });
    const preview = this.querySelector('#embedded-pdf-preview') as any;
    const iframe = preview?.querySelector('iframe') as HTMLIFrameElement | null;
    if (iframe?.contentWindow) {
      iframe.contentWindow.print();
    } else {
      window.print();
    }
  }

  private async handleDownload() {
    console.log("[REAL-PDF-FLOW]", {
      module: "RI",
      stage: "CLICK",
      action: "download",
      reportId: this.reportId
    });
    const preview = this.querySelector('#embedded-pdf-preview') as any;
    if (preview && preview._pdfBytes && preview._snapshot) {
      const { triggerBrowserDownload } = await import("../../infrastructure/documents/document-engine.js");
      const docNum = preview._snapshot.documentNumber || `${preview._snapshot.type}_${preview._snapshot.id}`;
      const finalDocNum = docNum.startsWith("RELATÓRIO DE INTEGRIDADE") ? docNum : `RELATÓRIO DE INTEGRIDADE ${docNum}`;
      triggerBrowserDownload(preview._pdfBytes, `${finalDocNum}.pdf`);
      return;
    }

    try {
      const { documentEngine } = await import("../../infrastructure/documents/document-engine.js");
      await import("../../infrastructure/documents/builders/index.js");
      const snapshot = this.getSnapshotData();
      await documentEngine.downloadPdf(snapshot, `RELATÓRIO DE INTEGRIDADE ${snapshot.documentNumber || this.reportId}.pdf`);
    } catch (e: any) {
      alert("Erro ao baixar PDF: " + e.message);
    }
  }

  private render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div style="padding: 72px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
          <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <span class="text-body-small">Carregando visualização oficial do relatório técnico...</span>
        </div>
      `;
      return;
    }

    if (this.errorMsg) {
      this.innerHTML = `
        <div style="padding: 32px; max-width: 560px; margin: 40px auto; background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: var(--radius-sm); color: var(--status-danger-text); text-align: center;">
          <div style="font-size: 1.125rem; font-weight: 600; margin-bottom: 8px;">Erro na Visualização do Laudo</div>
          <p style="color: var(--color-graphite-300); font-size: 0.9rem; margin-bottom: 20px;">${this.errorMsg}</p>
          <button class="btn-action btn-primary" id="btn-back-error" style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background: var(--color-graphite-700); border: none; border-radius: 6px; color: var(--color-text-primary); cursor: pointer;">
            <i data-lucide="x" style="width: 16px; height: 16px;"></i> Fechar
          </button>
        </div>
      `;
      createIcons({ icons: { X }, root: this });
      this.querySelector('#btn-back-error')?.addEventListener('click', this.closeReportViewer);
      return;
    }

    const rep = this.report || {};
    const eq = this.equipmentSnapshot?.equipmentData || {};
    const props = eq.properties || eq || {};
    const recs = this.recommendations || [];
    const status = rep.status || 'DRAFT';

    const formatVal = (val: any, fallback = '-') => {
      if (val && typeof val === 'object' && val.value !== undefined) {
        return `${val.value} ${val.unit || ''}`.trim();
      }
      return val || fallback;
    };
    
    const isPiping = eq.equipmentType?.toLowerCase().includes('tubulação') || eq.equipmentType?.toLowerCase().includes('piping') || eq.inServiceInspectionCode?.includes('570');
    const apiCode = eq.inServiceInspectionCode || (isPiping ? 'API 570' : 'API 510');
    const apiDesc = isPiping ? 'Piping Inspection Code' : 'Pressure Vessel Inspection Code';

    this.innerHTML = `
      <style>
        .report-viewer-container {
          min-height: 100vh;
          background: var(--color-graphite-800);
          color: var(--color-graphite-200);
          font-family: var(--font-sans, system-ui, -apple-system, sans-serif);
          display: flex;
          flex-direction: column;
        }

        /* Top Bar */
        .viewer-topbar {
          background: var(--color-graphite-800);
          border-bottom: 1px solid var(--color-primary);
          padding: 12px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 100;
          flex-wrap: wrap;
          gap: 12px;
        }

        .topbar-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .topbar-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .topbar-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .btn-view-action {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          border: 1px solid transparent;
        }

        .btn-view-action.primary {
          background: var(--ia-action-bg, var(--color-primary));
          color: var(--ia-action-fg, var(--color-text-primary));
        }
        .btn-view-action.primary:hover {
          background: var(--color-primary);
        }

        .btn-view-action.secondary {
          background: var(--color-primary);
          border-color: var(--color-text-muted);
          color: var(--color-graphite-300);
        }
        .btn-view-action.secondary:hover {
          background: var(--color-graphite-700);
          color: var(--color-text-primary);
        }

        .btn-view-action.success {
          background: var(--status-success-text);
          color: var(--color-text-primary);
        }
        .btn-view-action.success:hover {
          background: var(--status-success-text);
        }

        /* Mode Toggle */
        .mode-toggle {
          background: var(--color-primary);
          border: 1px solid var(--color-graphite-700);
          border-radius: 6px;
          padding: 2px;
          display: flex;
        }
        .mode-toggle-btn {
          background: transparent;
          border: none;
          color: var(--color-graphite-400);
          padding: 6px 12px;
          font-size: 0.8rem;
          font-weight: 600;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .mode-toggle-btn.active {
          background: var(--color-primary);
          color: var(--color-text-primary);
        }

        /* Zoom Control */
        .zoom-control {
          background: var(--color-primary);
          border: 1px solid var(--color-graphite-700);
          border-radius: 6px;
          display: flex;
          align-items: center;
          padding: 2px 6px;
          color: var(--color-graphite-300);
          font-size: 0.8rem;
          gap: 4px;
        }
        .zoom-btn {
          background: transparent;
          border: none;
          color: var(--color-graphite-400);
          padding: 4px 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }
        .zoom-btn:hover {
          color: var(--color-text-primary);
          background: var(--color-graphite-700);
        }

        /* Status Pill */
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 700;
        }
        .status-badge.issued { background: rgba(16, 185, 129, 0.2); color: var(--status-success-text); border: 1px solid var(--status-success-solid); }
        .status-badge.waiting { background: rgba(245, 158, 11, 0.2); color: var(--status-warning-text); border: 1px solid var(--status-warning-solid); }
        .status-badge.draft { background: rgba(148, 163, 184, 0.2); color: var(--color-graphite-300); border: 1px solid var(--color-graphite-600); }

        /* Main Workspace */
        .viewer-workspace {
          flex: 1;
          display: flex;
          justify-content: center;
          padding: 32px 16px;
          overflow-y: auto;
          background: var(--color-bg-primary);
        }

        /* A4 Digital Paper Sheet */
        .a4-sheet {
          width: 210mm;
          min-height: 297mm;
          padding: 18mm 18mm 16mm 18mm;
          margin-bottom: 24px;
          background: var(--color-surface-primary);
          color: var(--color-text-primary);
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.3);
          border-radius: 2px;
          box-sizing: border-box;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          font-size: 9.5pt;
          line-height: 1.45;
          transform-origin: top center;
        }

        /* Sheet Inner Elements */
        .sheet-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid var(--color-border-default);
          padding-bottom: 8px;
          margin-bottom: 12px;
        }
        .sheet-brand-title {
          font-size: 14pt;
          font-weight: 800;
          color: var(--color-text-primary);
          letter-spacing: -0.02em;
        }
        .sheet-brand-sub {
          font-size: 7.5pt;
          color: var(--color-text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .sheet-doc-title {
          font-size: 11pt;
          font-weight: 800;
          color: var(--color-text-primary);
          text-align: right;
        }
        .sheet-doc-sub {
          font-size: 7.5pt;
          color: var(--color-ink);
          text-align: right;
          font-weight: 600;
        }

        .sheet-footer {
          border-top: 1px solid var(--color-graphite-300);
          padding-top: 6px;
          display: flex;
          justify-content: space-between;
          font-size: 7.5pt;
          color: var(--color-text-secondary);
          margin-top: 16px;
        }

        .doc-section-title {
          background: var(--color-surface-elevated);
          color: var(--color-text-primary);
          font-weight: 700;
          font-size: 9pt;
          padding: 4px 8px;
          margin: 10px 0 6px 0;
          border-radius: 2px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .doc-sub-title {
          background: var(--color-surface-secondary);
          color: var(--color-text-muted);
          font-weight: 600;
          font-size: 8.5pt;
          padding: 3px 6px;
          border-left: 3px solid var(--color-ink);
          margin: 6px 0 4px 0;
        }

        .sheet-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 6px;
          margin-bottom: 8px;
        }
        .sheet-grid-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 6px;
          margin-bottom: 8px;
        }

        .sheet-field {
          background: var(--color-bg-primary);
          border: 1px solid var(--color-graphite-200);
          padding: 4px 8px;
          border-radius: 2px;
        }
        .sheet-field-label {
          font-size: 7pt;
          color: var(--color-text-secondary);
          text-transform: uppercase;
          font-weight: 600;
        }
        .sheet-field-value {
          font-size: 9pt;
          font-weight: 600;
          color: var(--color-text-muted);
        }

        .sheet-text-block {
          background: var(--color-bg-primary);
          border: 1px solid var(--color-graphite-200);
          padding: 6px 8px;
          font-size: 8.5pt;
          color: var(--color-text-muted);
          margin-bottom: 6px;
          border-radius: 2px;
        }

        .sheet-table {
          width: 100%;
          border-collapse: collapse;
          margin: 6px 0 10px 0;
          font-size: 8pt;
        }
        .sheet-table th {
          background: var(--color-graphite-200);
          color: var(--ia-primary);
          font-weight: 700;
          padding: 4px 6px;
          border: 1px solid var(--color-graphite-300);
          text-align: left;
        }
        .sheet-table td {
          padding: 4px 6px;
          border: 1px solid var(--color-graphite-200);
          color: var(--color-text-muted);
        }
        .sheet-table tr:nth-child(even) td {
          background: var(--color-bg-primary);
        }

        .seal-box {
          border: 1px dashed var(--color-border-default);
          background: rgba(14, 165, 233, 0.1);
          padding: 8px;
          border-radius: 4px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 10px;
        }

        /* Print Media Rules */
        @media print {
          body * {
            visibility: hidden;
          }
          .viewer-topbar, .mode-toggle, .zoom-control {
            display: none !important;
          }
          .viewer-workspace {
            padding: 0 !important;
            background: white !important;
            overflow: visible !important;
          }
          .a4-sheet, .a4-sheet * {
            visibility: visible;
          }
          .a4-sheet {
            position: relative;
            left: 0;
            top: 0;
            margin: 0 !important;
            box-shadow: none !important;
            page-break-after: always;
            width: 100% !important;
            min-height: 100vh !important;
            transform: none !important;
          }
        }
      </style>

      <div class="report-viewer-container">
        <!-- Top Navigation Bar -->
        <div class="viewer-topbar">
          <div class="topbar-left">
            <button class="btn-view-action secondary" id="btn-back">
              <i data-lucide="x" style="width: 16px; height: 16px;"></i> Fechar
            </button>
            <div>
              <h1 class="topbar-title">
                <i data-lucide="file-text" style="width: 20px; height: 20px; color: var(--color-text-muted);"></i>
                ${rep.id || this.reportId} • Laudo de Integridade NR-13
              </h1>
            </div>
            <span class="status-badge ${status === 'ISSUED' || status === 'APPROVED' ? 'issued' : (status === 'WAITING_PLH' || status === 'WAITING_SIGNATURE' ? 'waiting' : 'draft')}">
              ${status}
            </span>
          </div>

          <div class="topbar-right">
            <!-- Zoom Controls -->
            <div class="zoom-control">
              <button class="zoom-btn" id="btn-zoom-out" title="Reduzir"><i data-lucide="zoom-out" style="width: 14px; height: 14px;"></i></button>
              <span id="zoom-text" style="min-width: 44px; text-align: center;">${this.zoomLevel}%</span>
              <button class="zoom-btn" id="btn-zoom-in" title="Ampliar"><i data-lucide="zoom-in" style="width: 14px; height: 14px;"></i></button>
              <button class="zoom-btn" id="btn-zoom-reset" title="Ajustar 100%"><i data-lucide="maximize-2" style="width: 14px; height: 14px;"></i></button>
            </div>

            <button class="btn-view-action" id="btn-open-checklist" style="background:var(--color-graphite-800); border: 1px solid var(--color-ink); color: var(--color-text-muted);">
              <i data-lucide="check-circle-2" style="width: 16px; height: 16px;"></i> Checklist Técnico (${this.checklistComponents.length})
            </button>
            <button class="btn-view-action secondary" id="btn-print" title="Imprimir laudo técnico">
              <i data-lucide="printer" style="width: 16px; height: 16px;"></i> Imprimir
            </button>
            <button class="btn-view-action primary" id="btn-download" title="Baixar PDF oficial">
              <i data-lucide="download" style="width: 16px; height: 16px;"></i> Baixar PDF
            </button>
          </div>
        </div>

        ${!this.getValidation().canPrint ? `
          <div style="background: var(--status-warning-bg); border-bottom: 1px solid var(--status-warning-border); padding: 8px 16px; display: flex; align-items: center; justify-content: space-between; font-size: 0.85rem; color: var(--status-warning-text);">
            <div style="display: flex; align-items: center; gap: 8px;">
              <i data-lucide="alert-triangle" style="width: 16px; height: 16px; color: var(--status-warning-text); flex-shrink: 0;"></i>
              <span><strong>ATENÇÃO:</strong> Este laudo contém campos em branco ou pendências técnicas. O download está liberado, porém recomenda-se revisão.</span>
            </div>
            <button type="button" id="btn-banner-view-pendencies" style="background: var(--status-warning-text); border: none; color: var(--color-text-primary); border-radius: 4px; padding: 4px 10px; font-size: 0.75rem; font-weight: 700; cursor: pointer;">
              Ver ${this.getValidation().errors.length} Pendências
            </button>
          </div>
        ` : `
          <div style="background: rgba(16, 185, 129, 0.12); border-bottom: 1px solid rgba(16, 185, 129, 0.3); padding: 6px 16px; display: flex; align-items: center; gap: 8px; font-size: 0.8rem; color: var(--status-success-border);">
            <i data-lucide="check-circle-2" style="width: 15px; height: 15px; color: var(--status-success-text); flex-shrink: 0;"></i>
            <span><strong>LAUDO CONCLUÍDO E APTO PARA EMISSÃO:</strong> Todos os requisitos técnicos e validações foram atendidos com sucesso.</span>
          </div>
        `}

        <!-- Main Workspace -->
        <div class="viewer-workspace" id="viewer-workspace">
          <div style="width: 100%; max-width: 1000px; height: 85vh; background: var(--color-primary); border-radius: 8px; overflow: hidden; border: 1px solid var(--color-graphite-700); margin: 0 auto;">
              <app-pdf-preview id="embedded-pdf-preview" style="height: 100%; display: block;"></app-pdf-preview>
            </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { ArrowLeft, Download, Printer, Edit, CheckCircle2, FileText, Shield, Sparkles, ZoomIn, ZoomOut, Maximize2, Share2, Eye, RefreshCw, PenTool, XCircle, AlertTriangle, X },
      root: this
    });

    this.attachEventListeners();

    
      const pdfPreview = this.querySelector('#embedded-pdf-preview') as any;
      if (pdfPreview) {
        pdfPreview.snapshot = this.getSnapshotData();
      
    }
  }

  private attachEventListeners() {
    this.querySelector('#btn-back')?.addEventListener('click', this.closeReportViewer);

    this.querySelector('#btn-open-checklist')?.addEventListener('click', () => {
      router.navigate(`/reports/integrity/checklist/${this.reportId}`);
    });

    this.querySelector('#btn-banner-view-pendencies')?.addEventListener('click', () => {
      this.showPrintBlockedModal(this.getValidation());
    });

    this.querySelector('#btn-print')?.addEventListener('click', () => {
      this.handlePrint();
    });

    this.querySelector('#btn-download')?.addEventListener('click', () => {
      this.handleDownload();
    });

    
    
    this.querySelector('#btn-zoom-in')?.addEventListener('click', () => {
      if (this.zoomLevel < 150) {
        this.zoomLevel += 10;
        this.render();
      }
    });

    this.querySelector('#btn-zoom-out')?.addEventListener('click', () => {
      if (this.zoomLevel > 50) {
        this.zoomLevel -= 10;
        this.render();
      }
    });

    this.querySelector('#btn-zoom-reset')?.addEventListener('click', () => {
      this.zoomLevel = 100;
      this.render();
    });
  }
}

customElements.define('app-report-viewer-page', AppReportViewerPage);



